import React, { useState, useEffect } from 'react'
import { Typography, IconButton } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'
import FormatAlignLeftIcon from '@material-ui/icons/FormatAlignLeft'
import FormatAlignCenterIcon from '@material-ui/icons/FormatAlignCenter'
import FormatAlignRightIcon from '@material-ui/icons/FormatAlignRight'
import VerticalAlignTopIcon from '@material-ui/icons/VerticalAlignTop'
import VerticalAlignBottomIcon from '@material-ui/icons/VerticalAlignBottom'
import VerticalAlignCenterIcon from '@material-ui/icons/VerticalAlignCenter'

// Small controls shared by the single-field and multi-select properties panels.

// The value every target shares, or undefined when they differ.
export const commonValue = (targets, get) => {
  const first = get(targets[0])
  return targets.every((t) => get(t) === first) ? first : undefined
}

// `precision` controls the displayed decimal places AND how rounding is performed
// after stepping/typing. Defaults to 1 to preserve existing call sites (font size,
// offset steppers, etc.). Pass `precision=2` for canvas-percent coords (x, y,
// containerW, imageWidth, imageHeight) per the spec.
// Wrap means "more than one line": a single line is what Trim already gives you, and no
// cap at all is the mode that slices a line through the middle.
export const MIN_WRAP_LINES = 2
export const MAX_WRAP_LINES = 10

const H_ALIGN_ICONS = [
  { key: 'left', Icon: FormatAlignLeftIcon },
  { key: 'center', Icon: FormatAlignCenterIcon },
  { key: 'right', Icon: FormatAlignRightIcon },
]
const V_ALIGN_ICONS = [
  { key: 'top', Icon: VerticalAlignTopIcon },
  { key: 'middle', Icon: VerticalAlignCenterIcon },
  { key: 'bottom', Icon: VerticalAlignBottomIcon },
]

// The horizontal + vertical alignment toggles. Text uses them to place its glyphs in the
// box; the sponsor area uses them to place the block of logos (and a grid's short row).
export function AlignRow({ label = 'Align', hAlign = 'center', vAlign = 'middle', onChange }) {
  const toggle = (icons, current, prop) => icons.map(({ key, Icon }) => {
    const active = current === key
    return (
      <IconButton
        key={key}
        size='small'
        onClick={() => onChange({ [prop]: key })}
        style={{
          padding: 4,
          color: active ? '#fff' : '#888',
          background: active ? '#1976d2' : 'transparent',
          borderRadius: 4,
        }}
      >
        <Icon style={{ fontSize: 16 }} />
      </IconButton>
    )
  })

  return (
    <div style={{ padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography variant='caption' color='textSecondary' style={{ minWidth: 32 }}>{label}</Typography>
      {toggle(H_ALIGN_ICONS, hAlign, 'hAlign')}
      {toggle(V_ALIGN_ICONS, vAlign, 'vAlign')}
    </div>
  )
}

// `mixed` blanks the field (shown as "Mixed") while `value` stays the base for +/- steps.
export function StepperRow({ label, value, suffix = '', step = 1, precision = 1, mixed = false, onChange }) {
  const shown = Number.isInteger(value) && precision === 1 ? String(value) : value.toFixed(precision)
  const display = mixed ? '' : shown
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(display)

  const commit = () => {
    setEditing(false)
    const n = parseFloat(text)
    if (Number.isFinite(n)) onChange(+n.toFixed(precision))
  }

  return (
    <div style={{ padding: '2px 12px', display: 'flex', alignItems: 'center', gap: 4 }}>
      <Typography variant='caption' color='textSecondary' style={{ minWidth: 32 }}>{label}</Typography>
      <IconButton size='small' onClick={() => onChange(+(value - step).toFixed(precision))} style={{ padding: 2 }}>
        <RemoveIcon style={{ fontSize: 16 }} />
      </IconButton>
      <div style={{ minWidth: 40, display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
        <input
          type='text'
          value={editing ? text : display}
          placeholder={mixed ? 'Mixed' : undefined}
          onFocus={(e) => { setText(display); setEditing(true); e.target.select() }}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur()
          }}
          style={{
            width: 40, textAlign: 'right', border: 'none', background: 'transparent',
            fontSize: '0.75rem', fontFamily: 'inherit', color: 'inherit', padding: 0, outline: 'none',
          }}
        />
        {!mixed && <Typography variant='caption'>{suffix}</Typography>}
      </div>
      <IconButton size='small' onClick={() => onChange(+(value + step).toFixed(precision))} style={{ padding: 2 }}>
        <AddIcon style={{ fontSize: 16 }} />
      </IconButton>
    </div>
  )
}

// Free-text hex input that complements the <input type='color'> picker. The
// picker can only emit #RRGGBB (no alpha, no shorthand), so users who want to
// type "#fff" or paste a CSS color need a text field. Local state buffers
// in-progress typing — we only commit when the buffer matches a valid hex
// pattern (3, 4, 6, or 8 hex digits with a leading #). Invalid input is left
// in the buffer until blur, at which point we revert to the last committed
// value so the picker and field can never disagree.
export function HexColorInput({ value, placeholder, onChange }) {
  const [text, setText] = useState(value)
  useEffect(() => { setText(value) }, [value])
  const commit = (raw) => {
    let v = raw.trim()
    if (v && !v.startsWith('#')) v = `#${v}`
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v)) {
      onChange(v)
    } else {
      setText(value)
    }
  }
  return (
    <input
      type='text'
      value={text}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => commit(text)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur()
      }}
      style={{
        width: 64, fontSize: '0.75rem', fontFamily: 'monospace',
        border: '1px solid #ccc', borderRadius: 3, padding: '2px 4px',
        background: '#fff', outline: 'none',
      }}
    />
  )
}
